import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import LinkButton from '@atlaskit/button/link';

import { expVal, expValEquals } from '@confluence/feature-experiments';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';

import { useLoomCrossJoinUrl } from '../useLoomCrossJoinUrl.ts';
import type { EntryPointId } from '../LoomCrossSellPopupCard/BaseLoomPopupCard.tsx';
import { useFetchRequestAccessCapability, useRequestAccessService } from './useRequestAccess.ts';

export const i18n: {
	requestAccessSuccessFlagTitle: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	requestAccessSuccessFlagDescription: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	requestAccessErrorFlagTitle: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	requestAccessErrorFlagDescription: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	requestAccessErrorFlagActionTitle: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	requestAccessNote: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	requestAccessSuccessFlagTitle: {
		id: 'loom-utils.loom-cross-join-popup-card.request-access-success-flag.title',
		defaultMessage: 'Your admins were notified of your request',
		description: 'Title for the success flag after requesting access to Loom',
	},
	requestAccessSuccessFlagDescription: {
		id: 'loom-utils.loom-cross-join-popup-card.request-access-success-flag.description',
		defaultMessage: 'You’ll receive an email once they’ve approved it.',
		description: 'Description for the success flag after requesting access to Loom',
	},
	requestAccessErrorFlagTitle: {
		id: 'loom-utils.loom-cross-join-popup-card.request-access-error-flag.title',
		defaultMessage: "We couldn't add you to Loom",
		description: 'Title for the error flag after requesting access to Loom',
	},
	requestAccessErrorFlagDescription: {
		id: 'loom-utils.loom-cross-join-popup-card.request-access-error-flag.description',
		defaultMessage: "Try again in a few minutes. If you still aren't able to join, ",
		description: 'Description for the error flag after requesting access to Loom',
	},
	requestAccessErrorFlagActionTitle: {
		id: 'loom-utils.loom-cross-join-popup-card.request-access-error-flag.action.title',
		defaultMessage: 'contact support',
		description: 'Action to contact support',
	},
	requestAccessNote: {
		id: 'loom-utils.use-streamlining-cross-join.request-access-note.content',
		defaultMessage: "Hi, admin 👋 I'd like to use Loom to collaborate with my team.",
		description: 'Note sent along with access request to Loom admins',
	},
});

type Props = {
	entryPointId: EntryPointId;
	flags?: FlagsStateContainer;
	source: string;
	sourceAdType: string;
	sourceComponent?: string;
};

export const useStreamliningCrossJoin = ({
	entryPointId,
	flags,
	source,
	sourceAdType,
	sourceComponent,
}: Props): ((fromTrigger?: boolean) => Promise<void>) => {
	const intl = useIntl();
	const crossJoinHref = useLoomCrossJoinUrl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const requestAccess = useRequestAccessService({
		entryPointId,
		source,
		sourceAdType,
		note: intl.formatMessage(i18n.requestAccessNote),
	});
	const fetchRequestAccessCapability = useFetchRequestAccessCapability({
		entryPointId,
		source,
		sourceAdType,
	});

	return useCallback(
		async (fromTrigger: boolean = false) => {
			const capability = await fetchRequestAccessCapability();
			if (capability === 'REQUEST_ACCESS') {
				if (
					['control', 'variation'].includes(
						expVal('twcg-1241_streamlining_loom_cross_join', 'cohort', 'not-enrolled'),
					)
				) {
					if (fromTrigger) {
						// Open new tab for control and do nothing for variation
						if (expValEquals('twcg-1241_streamlining_loom_cross_join', 'cohort', 'control')) {
							window && window.open(crossJoinHref, '_blank');
						}
						return;
					}
					// fire manual feature exposed event for control and variation cohorts only
					createAnalyticsEvent({
						type: 'sendTrackEvent',
						data: {
							action: 'exposed',
							actionSubject: 'feature',
							source,
							attributes: {
								adType: sourceAdType,
								entrypoint: entryPointId,
								flagKey: 'twcg-1241_streamlining_loom_cross_join',
								cohort: expVal('twcg-1241_streamlining_loom_cross_join', 'cohort', 'not-enrolled'),
								fromTrigger, // we should always receive false here
							},
						},
					}).fire();

					// streamlining access request flow for variation cohort
					if (expValEquals('twcg-1241_streamlining_loom_cross_join', 'cohort', 'variation')) {
						try {
							const requestedAccess = await requestAccess();
							if (requestedAccess) {
								flags &&
									flags.showSuccessFlag({
										title: intl.formatMessage(i18n.requestAccessSuccessFlagTitle),
										description: intl.formatMessage(i18n.requestAccessSuccessFlagDescription),
									});

								// after successful access request, we do not proceed to open the cross join link
								return;
							}
						} catch (error: unknown) {
							createAnalyticsEvent({
								type: 'sendTrackEvent',
								data: {
									action: 'failed',
									actionSubject: 'requestAccess',
									source,
									attributes: {
										adType: sourceAdType,
										entrypoint: entryPointId,
										sourceComponent,
										error: error instanceof Error ? error.message : 'unknown',
									},
								},
							}).fire();

							flags &&
								flags.showErrorFlag({
									title: intl.formatMessage(i18n.requestAccessErrorFlagTitle),
									description: (
										<>
											<span>{intl.formatMessage(i18n.requestAccessErrorFlagDescription)}</span>
											<LinkButton
												href="https://support.atlassian.com/contact"
												target="_blank"
												rel="noreferrer"
											>
												{intl.formatMessage(i18n.requestAccessErrorFlagActionTitle)}
											</LinkButton>
										</>
									),
								});
						}
					}
				}
			}

			// for other capability, we bring back the default link behaviour here
			// as we have disabled the default link behaviour for the trigger button
			window && window.open(crossJoinHref, '_blank');
		},
		[
			createAnalyticsEvent,
			crossJoinHref,
			entryPointId,
			fetchRequestAccessCapability,
			flags,
			intl,
			requestAccess,
			source,
			sourceAdType,
			sourceComponent,
		],
	);
};
